/**
 * Supabase implementation of the portal's {@link Subscriptions} contract —
 * Phase 4b of the Firestore→Postgres migration
 * (docs/plans/2026-07-11-002-feat-postgres-migration-plan.md).
 *
 * Interface-identical to the Firestore `buildSubscriptions` (firebase.ts), so
 * Gallery/Studio/GamePage/IteratePanel/Admin/Comments consume it unchanged at
 * cutover. Three binding strategies, chosen per table:
 *
 *  1. LIVESET   — tables whose Realtime payloads carry the complete row
 *     (llmRequests, actionLog, creatorProfiles, comments):
 *     initial PostgREST select seeds a {@link LiveSet}; each postgres_changes
 *     event is mapped row→doc and applied; the merged ordered/limited set
 *     re-emits. No refetch traffic.
 *
 *  2. REFETCH   — creations lists (gallery / admin / myCreations) and the
 *     single-creation doc: rows need the `creation_members` embed (editors +
 *     memberUids reconstruction), which change events don't carry — so events
 *     (on creations AND creation_members) trigger a DEBOUNCED refetch of the
 *     PostgREST query. Correct-by-construction; cheap at catalog scale.
 *
 *  3. EXISTENCE — isLiked: a filtered single-row select + INSERT/DELETE events.
 *
 *  4. POLL — browser-safe views and small sets intentionally kept outside the
 *     managed realtime publication. This includes the admin build-job monitor:
 *     browsers cannot read the authority-bearing base table or subscribe to its
 *     changes, so the explicit safe projection is refreshed at a bounded rate.
 *
 * RLS notes: subscriber visibility == the 0003 policies (a denied/filtered
 * monitor stream simply stays empty; channel failures surface via onError,
 * mirroring the Firestore snapshotError contract). DELETE events on RLS tables
 * carry only the PRIMARY KEY in `old` — every LiveSet below keys on the pk.
 */
import type {
  RealtimeChannel,
  RealtimePostgresChangesPayload,
  SupabaseClient,
} from '@supabase/supabase-js';
import type {
  ActionLogEntry,
  AssetGenJob,
  AuditJob,
  BadgeMetric,
  BuildJob,
  ChatMessage,
  Comment,
  Creation,
  CreationFeedback,
  CreationVersion,
  CreationFilmRunV1,
  CreationFilmMaterialV1,
  CreatorProfile,
  LlmRequestDoc,
  MatchInvite,
  UserPresence,
  StripAssetMeta,
  UserBadge,
} from '@djgg/creations';
import { CHAT_PAGE_SIZE, validateCreationFilmRunV1, validateCreationFilmMaterialV1 } from '@djgg/creations';
import {
  CREATIONS_WITH_MEMBERS_SELECT,
  LiveSet,
  TABLES,
  byNumber,
  rowToDoc,
  stitchCreationMembers,
  type ChangeEvent,
  type MemberRow,
  type Row,
} from '@djgg/db/browser';
import type { Unsubscribe } from '@djgg/core';
import type {
  AssetVariant,
  BadgeProgressRow,
  GameFan,
  GameRecentPlayer,
  PublicLlmStatRow,
  PublicStats,
  PublicStatsTotals,
  PublicUserProfileSummary,
  PlatformLeaderboardEntry,
  PlatformLeaderboardPage,
  PlatformLeaderboardView,
  SubscriptionErrorHandler,
  Subscriptions,
} from './types.js';

type AnyPayload = RealtimePostgresChangesPayload<Row>;

/**
 * Channel topics must be UNIQUE per binding instance: `client.channel(topic)`
 * returns the EXISTING channel when the topic repeats (realtime-js dedupes by
 * topic), and adding `postgres_changes` callbacks to an already-subscribed
 * channel THROWS — two components subscribed to the same doc (IteratePanel +
 * GameVocabulary on the same version) blanked the whole studio. The label
 * stays for diagnostics; the counter guarantees independence, including from
 * a remounting component whose previous unsubscribe is still in flight.
 */
let channelSeq = 0;
function channelTopic(label: string): string {
  channelSeq += 1;
  return `portal:${label}#${channelSeq}`;
}

/** Mirror of firebase.ts snapshotError: never let a failed stream go uncaught. */
function streamError(label: string, onError?: SubscriptionErrorHandler): (e: unknown) => void {
  return (e: unknown) => {
    if (onError) onError(e);
    else console.warn(`[portal] subscription '${label}' error:`, e);
  };
}

/** Subscribe a channel and route terminal failures to onError. */
function openChannel(
  channel: RealtimeChannel,
  fail: (e: unknown) => void,
): Unsubscribe {
  channel.subscribe((status) => {
    if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
      fail(new Error(`realtime channel ${status}`));
    }
  });
  return () => {
    void channel.unsubscribe();
  };
}

/** Map a creations row (with embedded members) to the domain Creation doc. */
function creationFromRow(row: Row): Creation & { id: string } {
  const { creation_members: members, member_filter: _mf, ...rest } = row as Row & {
    creation_members?: MemberRow[];
    member_filter?: unknown;
  };
  const doc = stitchCreationMembers(rowToDoc(TABLES.creations, rest), members ?? []);
  return { ...(doc as unknown as Creation), id: String(row.id) };
}

function creationFilmRunFromRow(row: Row): CreationFilmRunV1 {
  return validateCreationFilmRunV1({
    schemaVersion: Number(row.schema_version) as 1,
    runId: String(row.run_id),
    creationId: String(row.creation_id),
    seed: Number(row.seed),
    binding: row.binding as CreationFilmRunV1['binding'],
    status: String(row.status) as CreationFilmRunV1['status'],
    materialCount: Number(row.material_count),
    createdAt: Number(row.created_at),
    updatedAt: Number(row.updated_at),
    ...(row.failure ? { failure: row.failure as NonNullable<CreationFilmRunV1['failure']> } : {}),
  });
}

function creationFilmMaterialFromRow(row: Row): CreationFilmMaterialV1 {
  return validateCreationFilmMaterialV1({
    schemaVersion: Number(row.schema_version) as 1,
    runId: String(row.run_id),
    materialId: String(row.material_id),
    creationId: String(row.creation_id),
    kind: String(row.kind) as CreationFilmMaterialV1['kind'],
    source: String(row.source) as CreationFilmMaterialV1['source'],
    title: String(row.title),
    ...(row.uri ? { uri: String(row.uri) } : {}),
    ...(row.text ? { text: String(row.text) } : {}),
    ...(row.deej_asset_id ? { deejAssetId: String(row.deej_asset_id) } : {}),
    ...(row.content_type ? { contentType: String(row.content_type) } : {}),
    ...(row.width != null ? { width: Number(row.width) } : {}),
    ...(row.height != null ? { height: Number(row.height) } : {}),
    ...(row.duration_ms != null ? { durationMs: Number(row.duration_ms) } : {}),
    ...(row.strip ? { strip: row.strip as CreationFilmMaterialV1['strip'] } : {}),
    ...(row.description ? { description: String(row.description) } : {}),
    ...(row.unavailable_reason ? { unavailableReason: String(row.unavailable_reason) } : {}),
    createdAt: Number(row.created_at),
  });
}

/** Strategy 2: PostgREST refetch on any relevant change, debounced. */
function refetchBinding(
  client: SupabaseClient,
  label: string,
  /** table name, optionally with a server-side realtime row filter. */
  tables: Array<string | { table: string; filter: string }>,
  refetch: () => Promise<void>,
  onError?: SubscriptionErrorHandler,
): Unsubscribe {
  const fail = streamError(label, onError);
  let timer: ReturnType<typeof setTimeout> | null = null;
  let closed = false;
  const kick = () => {
    if (closed) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      refetch().catch(fail);
    }, 150);
  };
  let channel = client.channel(channelTopic(label));
  for (const t of tables) {
    const spec = typeof t === 'string' ? { table: t } : t;
    channel = channel.on(
      'postgres_changes',
      { event: '*', schema: 'public', ...spec },
      kick,
    );
  }
  const closeChannel = openChannel(channel, fail);
  refetch().catch(fail);
  return () => {
    closed = true;
    if (timer) clearTimeout(timer);
    closeChannel();
  };
}

/** Strategy 1: seed a LiveSet from PostgREST, apply Realtime events, emit. */
function liveSetBinding<T>(
  client: SupabaseClient,
  opts: {
    label: string;
    table: string;
    registryKey: string;
    /** PostgREST filter (col, value) equalities; also applied to events. */
    eq?: Array<[string, string | number]>;
    order?: { column: string; ascending: boolean };
    limit?: number;
    /** Realtime server-side filter (single `col=eq.value`). */
    realtimeFilter?: string;
    keyColumns: string[];
    /**
     * Supabase Realtime automatically rejoins channels after transient socket
     * failures. Keep the last good rows visible and resync once that rejoin
     * succeeds instead of surfacing a permanent load error.
     */
    recoverRealtimeErrors?: boolean;
  },
  cb: (rows: Array<T & { id: string }>) => void,
  onError?: SubscriptionErrorHandler,
): Unsubscribe {
  const fail = streamError(opts.label, onError);
  const def = TABLES[opts.registryKey];
  const keyOf = (r: Row) => opts.keyColumns.map((c) => String(r[c])).join('/');
  const compare = opts.order
    ? byNumber<Row>(opts.order.column, opts.order.ascending ? 'asc' : 'desc')
    : undefined;
  const set = new LiveSet<Row>(keyOf, compare, opts.limit);
  let seeded = false;
  let refreshing = false;
  let resyncAfterRefresh = false;
  let closed = false;
  // Events can arrive while the seed SELECT is in flight; a row committed
  // after the SELECT's snapshot would otherwise be lost forever (review
  // finding) — buffer and apply after the seed lands.
  const preSeed: AnyPayload[] = [];

  const emit = () =>
    cb(
      set.rows().map((r) => ({
        ...(rowToDoc(def, r) as T),
        id: String(r[def.docIdColumn]),
      })),
    );

  const matches = (r: Row | null | undefined): boolean =>
    !!r && (opts.eq ?? []).every(([col, v]) => String(r[col]) === String(v));

  // An UPDATE whose NEW row no longer matches the set's filter is a REMOVAL, not
  // a no-op — e.g. an invite going status open→accepted must drop off the board.
  // Synthesize a DELETE (keyed off the now-non-matching row) so LiveSet evicts
  // it. Subscriptions filtering on IMMUTABLE columns (creation_id/uid) never hit
  // this branch, so their behavior is unchanged.
  const asChange = (p: AnyPayload): ChangeEvent =>
    p.eventType !== 'DELETE' && !matches(p.new as Row)
      ? { eventType: 'DELETE', new: null, old: (p.new ?? null) as Row | null }
      : {
          eventType: p.eventType,
          new: (p.new ?? null) as Row | null,
          old: (p.old ?? null) as Row | null,
        };

  const onEvent = (p: AnyPayload) => {
    if (!seeded || refreshing) {
      preSeed.push(p);
      return;
    }
    if (set.apply(asChange(p))) emit();
  };

  const refresh = async (): Promise<void> => {
    if (closed || refreshing) return;
    refreshing = true;
    try {
      let q = client.from(opts.table).select('*');
      for (const [col, v] of opts.eq ?? []) q = q.eq(col, v);
      if (opts.order) q = q.order(opts.order.column, { ascending: opts.order.ascending });
      if (opts.limit) q = q.limit(opts.limit);
      const { data, error } = await q;
      if (error) throw error;
      if (closed) return;
      set.seed((data ?? []) as Row[]);
      seeded = true;
      for (const p of preSeed.splice(0)) set.apply(asChange(p));
      emit();
    } finally {
      refreshing = false;
      if (!closed && resyncAfterRefresh) {
        resyncAfterRefresh = false;
        void refresh().catch(reportFailure);
      }
    }
  };
  const reportFailure = (error: unknown) => {
    if (!closed) fail(error);
  };

  let channel = client.channel(channelTopic(opts.label)).on(
    'postgres_changes',
    {
      event: '*',
      schema: 'public',
      table: opts.table,
      ...(opts.realtimeFilter ? { filter: opts.realtimeFilter } : {}),
    },
    onEvent,
  );
  let closeChannel: Unsubscribe;
  if (opts.recoverRealtimeErrors) {
    let needsResync = false;
    channel.subscribe((status) => {
      if (closed) return;
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        needsResync = true;
        console.warn(`[portal] subscription '${opts.label}' reconnecting after ${status}`);
      } else if (status === 'SUBSCRIBED' && needsResync) {
        needsResync = false;
        if (refreshing) resyncAfterRefresh = true;
        else void refresh().catch(reportFailure);
      }
    });
    closeChannel = () => {
      void channel.unsubscribe();
    };
  } else {
    closeChannel = openChannel(channel, fail);
  }

  void refresh().catch(reportFailure);

  return () => {
    closed = true;
    closeChannel();
  };
}

/** Small-set polling for tables intentionally kept out of the managed publication. */
function pollingRowsBinding<T>(
  client: SupabaseClient,
  opts: {
    label: string;
    table: string;
    registryKey: string;
    eq?: Array<[string, string | number]>;
    order?: { column: string; ascending: boolean };
    limit?: number;
    intervalMs: number;
  },
  cb: (rows: Array<T & { id: string }>) => void,
  onError?: SubscriptionErrorHandler,
): Unsubscribe {
  const fail = streamError(opts.label, onError);
  const def = TABLES[opts.registryKey];
  let closed = false;
  let inFlight = false;

  const refresh = async (): Promise<void> => {
    if (closed || inFlight) return;
    inFlight = true;
    try {
      let q = client.from(opts.table).select('*');
      for (const [col, value] of opts.eq ?? []) q = q.eq(col, value);
      if (opts.order) q = q.order(opts.order.column, { ascending: opts.order.ascending });
      if (opts.limit) q = q.limit(opts.limit);
      const { data, error } = await q;
      if (error) throw error;
      if (closed) return;
      cb((data ?? []).map((row) => ({
        ...(rowToDoc(def, row as Row) as T),
        id: String((row as Row)[def.docIdColumn]),
      })));
    } finally {
      inFlight = false;
    }
  };

  const reportFailure = (error: unknown) => {
    if (!closed) fail(error);
  };
  void refresh().catch(reportFailure);
  const timer = setInterval(() => void refresh().catch(reportFailure), opts.intervalMs);
  return () => {
    closed = true;
    clearInterval(timer);
  };
}

/** Coerce a PostgREST value to a number (bigint aggregates may arrive as strings). */
const num = (v: unknown): number => (v == null ? 0 : Number(v));

/**
 * One-shot read of the public stats VIEWS (`public_stats` + `public_llm_stats`,
 * migration 0017) — anon-granted aggregates over otherwise-restricted tables.
 * The Stats page fetches on mount; no realtime (views don't stream).
 */
export async function fetchPublicStats(client: SupabaseClient): Promise<PublicStats> {
  const [totalsRes, llmRes] = await Promise.all([
    client.from('public_stats').select('*').single(),
    client.from('public_llm_stats').select('*'),
  ]);
  if (totalsRes.error) throw totalsRes.error;
  if (llmRes.error) throw llmRes.error;
  const t = totalsRes.data as Record<string, unknown>;
  const totals: PublicStatsTotals = {
    uniqueSessions: num(t.unique_sessions),
    pageViews: num(t.page_views),
    totalPlays: num(t.total_plays),
    gameViews: num(t.game_views),
    multiplayerMatches: num(t.multiplayer_matches),
    gamesCreated: num(t.games_created),
    nativeGames: num(t.native_games),
    completedForks: num(t.completed_forks),
    gameIterations: num(t.game_iterations),
    assetsAttached: num(t.assets_attached),
    assetVariantsGenerated: num(t.asset_variants_generated),
    totalUsers: num(t.total_users),
    totalLikes: num(t.total_likes),
    totalComments: num(t.total_comments),
  };
  const llm: PublicLlmStatRow[] = ((llmRes.data ?? []) as Array<Record<string, unknown>>).map(
    (r) => ({
      provider: String(r.provider ?? ''),
      model: String(r.model ?? ''),
      task: String(r.task ?? ''),
      calls: num(r.calls),
      inputTokens: num(r.input_tokens),
      outputTokens: num(r.output_tokens),
      estimatedCostUsd: num(r.estimated_cost_usd),
    }),
  );
  return { totals, llm };
}

const LEADERBOARD_ORDER: Record<PlatformLeaderboardView, string> = {
  overall: 'total_xp', champions: 'champion_xp', makers: 'maker_xp', explorers: 'explorer_xp', recent: 'recent_xp',
};

function platformLeaderboardEntry(row: Record<string, unknown>): PlatformLeaderboardEntry {
  return {
    uid: String(row.uid ?? ''), handle: String(row.handle ?? ''), avatarKey: String(row.avatar_key ?? ''),
    joinedAt: num(row.joined_at), lastActiveAt: row.last_seen_at == null ? null : num(row.last_seen_at),
    badgesXp: num(row.badges_xp), explorerXp: num(row.explorer_xp), makerXp: num(row.maker_xp), championXp: num(row.champion_xp),
    recentXp: num(row.recent_xp), totalXp: num(row.total_xp), gamesCompleted: num(row.games_completed), gamesCreated: num(row.games_created), boardsTopped: num(row.boards_topped),
    overallRank: num(row.overall_rank), championRank: num(row.champion_rank), makerRank: num(row.maker_rank), explorerRank: num(row.explorer_rank), recentRank: num(row.recent_rank),
  };
}

/** Read the database's public, derived Community Rankings view. */
export async function fetchPlatformLeaderboard(
  client: SupabaseClient,
  input: { view: PlatformLeaderboardView; query?: string; page?: number; pageSize?: number; uid?: string },
): Promise<PlatformLeaderboardPage> {
  const page = Math.max(0, input.page ?? 0);
  const pageSize = Math.min(100, Math.max(1, input.pageSize ?? 50));
  let list = client.from('public_platform_leaderboard').select('*', { count: 'exact' });
  const query = input.query?.trim();
  if (query) list = list.ilike('handle', `%${query.replaceAll('%', '\\%').replaceAll('_', '\\_')}%`);
  const { data, error, count } = await list.order(LEADERBOARD_ORDER[input.view], { ascending: false }).order('handle', { ascending: true })
    .range(page * pageSize, page * pageSize + pageSize - 1);
  if (error) throw error;
  let me: PlatformLeaderboardEntry | null = null;
  if (input.uid) {
    const mine = await client.from('public_platform_leaderboard').select('*').eq('uid', input.uid).maybeSingle();
    if (mine.error) throw mine.error;
    me = mine.data ? platformLeaderboardEntry(mine.data as Record<string, unknown>) : null;
  }
  return { entries: ((data ?? []) as Array<Record<string, unknown>>).map(platformLeaderboardEntry), total: count ?? 0, me };
}

/**
 * One-shot read of the CALLING user's badge progress (`my_badge_progress`,
 * migration 0023 — the fetchPublicStats pattern over an owner-rights view).
 * The view self-filters on the signed-in uid, so anon and other users simply
 * get zero-value rows for nobody; no realtime (views don't stream). Fetched
 * when a user opens their OWN badge wall.
 */
export async function fetchMyBadgeProgress(client: SupabaseClient): Promise<BadgeProgressRow[]> {
  const { data, error } = await client.from('my_badge_progress').select('*');
  if (error) throw error;
  return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
    metric: String(r.metric ?? '') as BadgeMetric,
    value: num(r.value),
  }));
}

export async function fetchPublicUserProfileSummary(
  client: SupabaseClient,
  uid: string,
): Promise<PublicUserProfileSummary> {
  const [summaryResult, recentResult] = await Promise.all([
    client.from('public_user_profile_summaries').select('*').eq('uid', uid).single(),
    client
      .from('public_user_profile_recent_games')
      .select('*')
      .eq('uid', uid)
      .order('last_played_at', { ascending: false }),
  ]);
  if (summaryResult.error) throw summaryResult.error;
  if (recentResult.error) throw recentResult.error;
  const row = summaryResult.data as Record<string, unknown>;
  return {
    uid,
    lastActiveAt: row.last_seen_at == null ? null : num(row.last_seen_at),
    gamesCreated: num(row.games_created),
    gamesPlayed: num(row.games_played),
    recentGames: ((recentResult.data ?? []) as Array<Record<string, unknown>>).map((recent) => ({
      gameKey: String(recent.game_key ?? ''),
      creationId: String(recent.creation_id ?? ''),
      slug: String(recent.slug ?? ''),
      title: String(recent.title ?? ''),
      lastPlayedAt: num(recent.last_played_at),
    })),
  };
}

/**
 * One-shot read of a game's RECENT PLAYERS (`public_game_recent_players`, 0047
 * amended by 0048) — the game page's "Recently played by" rail, freshest first.
 * The view is capped at 5 per game and already applies every gate (public+live
 * games, real claimed players; members are listed like anyone else), so the
 * caller renders exactly what comes back. Public read, no realtime (views don't
 * stream): a rail of who played EARLIER has nothing to gain from live updates —
 * who is playing right now is the presence rail's job.
 *
 * `gameKey` is the creation id, or the slug for a native (the recordGameFinish
 * vocabulary the presence beat writes); the view resolves both.
 */
export async function fetchGameRecentPlayers(
  client: SupabaseClient,
  gameKey: string,
): Promise<GameRecentPlayer[]> {
  const { data, error } = await client
    .from('public_game_recent_players')
    .select('*')
    .eq('game_key', gameKey)
    .order('last_played_at', { ascending: false });
  if (error) throw error;
  return ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
    uid: String(row.uid ?? ''),
    handle: String(row.handle ?? ''),
    avatarKey: String(row.avatar_key ?? ''),
    lastPlayedAt: num(row.last_played_at),
  }));
}

/**
 * One-shot read of a game's fans — everyone who currently likes it, newest
 * first, over the `public_game_fans` view (0067). Same contract as
 * {@link fetchGameRecentPlayers}: the view owns eligibility, the caller
 * renders what comes back, and a list of who liked EARLIER gains nothing
 * from realtime. `gameKey` is the creation id the page likes/unlikes with
 * (`native-<slug>` for platform games).
 */
export async function fetchGameFans(
  client: SupabaseClient,
  gameKey: string,
): Promise<GameFan[]> {
  const { data, error } = await client
    .from('public_game_fans')
    .select('*')
    .eq('game_key', gameKey)
    .order('liked_at', { ascending: false });
  if (error) throw error;
  return ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
    uid: String(row.uid ?? ''),
    handle: String(row.handle ?? ''),
    avatarKey: String(row.avatar_key ?? ''),
    likedAt: num(row.liked_at),
  }));
}

/**
 * One-shot read of a creation's asset VARIANTS (member RLS, migration 0029),
 * newest first — the studio resolves a completed generation job's variants (by
 * `genJobId`) so they stay attachable after a reload/navigation. Optional
 * name/kind/review-state scoping for a per-asset recovery view.
 */
export async function fetchAssetVariants(
  client: SupabaseClient,
  creationId: string,
  opts: {
    genJobId?: string;
    name?: string;
    kind?: string;
    reviewState?: 'pending' | 'attached' | 'skipped' | 'superseded' | 'rejected';
  } = {},
): Promise<Array<AssetVariant & { genJobId?: string }>> {
  let q = client.from('asset_variants').select('*')
    .eq('creation_id', creationId)
    .eq('exposure_state', 'active');
  if (opts.genJobId !== undefined) q = q.eq('gen_job_id', opts.genJobId);
  if (opts.name !== undefined) q = q.eq('name', opts.name);
  if (opts.kind !== undefined) q = q.eq('kind', opts.kind);
  if (opts.reviewState !== undefined) q = q.eq('review_state', opts.reviewState);
  const { data, error } = await q.order('created_at', { ascending: false });
  if (error) throw error;
  return ((data ?? []) as Row[]).map((row) => {
    const doc = rowToDoc(TABLES['creations/assetVariants'], row) as unknown as {
      variantId: string;
      url: string;
      rawPreviewUrl?: string | null;
      prompt: string;
      kind: string;
      name: string;
      genJobId?: string;
      strip?: StripAssetMeta;
      reviewState?: 'pending' | 'attached' | 'skipped' | 'superseded' | 'rejected';
    };
    return {
      id: doc.variantId,
      url: doc.url,
      ...(doc.rawPreviewUrl ? { rawPreviewUrl: doc.rawPreviewUrl } : {}),
      prompt: doc.prompt,
      kind: doc.kind,
      name: doc.name,
      ...(doc.genJobId ? { genJobId: doc.genJobId } : {}),
      ...(doc.strip ? { strip: doc.strip } : {}),
      ...(doc.reviewState ? { reviewState: doc.reviewState } : {}),
    };
  });
}

/** Build the Supabase-backed subscription bundle (Firestore-interface-identical). */
export function buildSupabaseSubscriptions(client: SupabaseClient): Subscriptions {
  /** Shared shape for the three creations lists (embed members, then filter). */
  function creationsList(
    label: string,
    cb: (rows: Array<Creation & { id: string }>) => void,
    onError: SubscriptionErrorHandler | undefined,
    build: (q: ReturnType<ReturnType<SupabaseClient['from']>['select']>) => typeof q,
  ): Unsubscribe {
    return refetchBinding(
      client,
      label,
      ['creations', 'creation_members'],
      async () => {
        const { data, error } = await build(
          client.from('creations').select(CREATIONS_WITH_MEMBERS_SELECT),
        );
        if (error) throw error;
        cb(((data ?? []) as Row[]).map(creationFromRow));
      },
      onError,
    );
  }

  return {
    creations(cb, onError) {
      return creationsList('creations', cb, onError, (q) => q.eq('visibility', 'public'));
    },
    allCreations(cb, onError) {
      return creationsList('allCreations', cb, onError, (q) => q);
    },
    myCreations(uid, cb, onError) {
      // membership inner-join filter — the Postgres form of `array-contains`
      return refetchBinding(
        client,
        `myCreations:${uid}`,
        ['creations', 'creation_members'],
        async () => {
          const { data, error } = await client
            .from('creations')
            .select(`${CREATIONS_WITH_MEMBERS_SELECT}, member_filter:creation_members!inner(uid)`)
            .eq('member_filter.uid', uid);
          if (error) throw error;
          cb(((data ?? []) as unknown as Row[]).map(creationFromRow));
        },
        onError,
      );
    },
    creation(id, cb, onError) {
      return refetchBinding(
        client,
        `creation:${id}`,
        // server-side row filters: a busy game page must not refetch on every
        // OTHER game's counter bump (refetch-storm review finding)
        [
          { table: 'creations', filter: `id=eq.${id}` },
          { table: 'creation_members', filter: `creation_id=eq.${id}` },
        ],
        async () => {
          const { data, error } = await client
            .from('creations')
            .select(CREATIONS_WITH_MEMBERS_SELECT)
            .eq('id', id)
            .maybeSingle();
          if (error) throw error;
          cb(data ? creationFromRow(data as unknown as Row) : null);
        },
        onError,
      );
    },
    versions(id, cb, onError) {
      return pollingRowsBinding<CreationVersion>(
        client,
        {
          label: `versions:${id}`,
          table: 'creation_versions_client',
          registryKey: 'creations/versions',
          eq: [['creation_id', id]],
          order: { column: 'n', ascending: false },
          intervalMs: 2_000,
        },
        cb,
        onError,
      );
    },
    version(id, n, cb, onError) {
      return pollingRowsBinding<CreationVersion>(
        client,
        {
          label: `version:${id}:${n}`,
          table: 'creation_versions_client',
          registryKey: 'creations/versions',
          eq: [['creation_id', id], ['n', n]],
          intervalMs: 2_000,
        },
        rows => cb(rows[0] ?? null),
        onError,
      );
    },
    creationFilmRuns(creationId, cb, onError) {
      return refetchBinding(
        client,
        `creationFilmRuns:${creationId}`,
        [{ table: 'creation_film_runs', filter: `creation_id=eq.${creationId}` }],
        async () => {
          const { data, error } = await client.from('creation_film_runs').select('*')
            .eq('creation_id', creationId).order('updated_at', { ascending: false }).limit(12);
          if (error) throw error;
          cb(((data ?? []) as Row[]).map(creationFilmRunFromRow));
        },
        onError,
      );
    },
    creationFilmMaterials(runId, cb, onError) {
      return refetchBinding(
        client,
        `creationFilmMaterials:${runId}`,
        [{ table: 'creation_film_materials', filter: `run_id=eq.${runId}` }],
        async () => {
          const { data, error } = await client.from('creation_film_materials').select('*')
            .eq('run_id', runId).order('created_at', { ascending: true }).limit(96);
          if (error) throw error;
          cb(((data ?? []) as Row[]).map(creationFilmMaterialFromRow));
        },
        onError,
      );
    },
    buildJobs(cb, onError) {
      return pollingRowsBinding<BuildJob>(
        client,
        {
          label: 'buildJobs',
          table: 'build_jobs_monitor',
          registryKey: 'buildJobs',
          order: { column: 'created_at', ascending: false },
          // The monitor view is the whole build history (done/failed rows are never
          // deleted), not a queue. The monitor needs what is in flight plus
          // recent outcomes; the reaper recycles stale claims and stuck queued
          // jobs, so a live job never ages out of this window. Keep this bounded
          // rather than granting browser access to the authority-bearing table.
          limit: 50,
          intervalMs: 2_000,
        },
        cb,
        onError,
      );
    },
    assetGenJobs(creationId, cb, onError) {
      return liveSetBinding<AssetGenJob>(
        client,
        {
          label: `assetGenJobs:${creationId}`,
          table: 'asset_gen_jobs',
          registryKey: 'assetGenJobs',
          eq: [['creation_id', creationId]],
          order: { column: 'created_at', ascending: false },
          // 80, not 40: a build's strip fan-out (up to STRIP_CAP=15 strip jobs
          // + regen statics in one wave) plus normal studio traffic must not
          // evict succeeded-but-unreviewed jobs out of the window — an evicted
          // row's variants silently vanish from the pending-review shelf.
          limit: 80,
          realtimeFilter: `creation_id=eq.${creationId}`,
          keyColumns: ['id'],
        },
        cb,
        onError,
      );
    },
    auditJobs(creationId, cb, onError) {
      return liveSetBinding<AuditJob>(
        client,
        {
          label: `auditJobs:${creationId}`,
          table: 'audit_jobs',
          registryKey: 'auditJobs',
          eq: [['creation_id', creationId]],
          order: { column: 'created_at', ascending: false },
          // One creation audits rarely (dedupe holds one outstanding job per
          // version) — a short window comfortably covers the publish control's
          // watch plus recent history.
          limit: 20,
          realtimeFilter: `creation_id=eq.${creationId}`,
          keyColumns: ['id'],
        },
        cb,
        onError,
      );
    },
    llmRequests(cb, onError) {
      return liveSetBinding<LlmRequestDoc>(
        client,
        {
          label: 'llmRequests',
          table: 'llm_requests',
          registryKey: 'llmRequests',
          order: { column: 'created_at', ascending: false },
          limit: 300,
          keyColumns: ['id'],
        },
        cb,
        onError,
      );
    },
    actionLog(cb, onError) {
      return liveSetBinding<ActionLogEntry>(
        client,
        {
          label: 'actionLog',
          table: 'action_log',
          registryKey: 'actionLog',
          order: { column: 'created_at', ascending: false },
          limit: 300,
          keyColumns: ['id'],
          recoverRealtimeErrors: true,
        },
        cb,
        onError,
      );
    },
    creatorProfiles(cb, onError) {
      return liveSetBinding<CreatorProfile>(
        client,
        {
          label: 'creatorProfiles',
          table: 'creator_profiles',
          registryKey: 'creatorProfiles',
          keyColumns: ['uid'],
        },
        cb,
        onError,
      );
    },
    userBadges(uid, cb, onError) {
      return liveSetBinding<UserBadge>(
        client,
        {
          label: `userBadges:${uid}`,
          table: 'user_badges',
          registryKey: 'users/badges',
          eq: [['uid', uid]],
          order: { column: 'awarded_at', ascending: true },
          realtimeFilter: `uid=eq.${uid}`,
          keyColumns: ['uid', 'badge_id'],
        },
        cb,
        onError,
      );
    },
    isLiked(creationId, uid, cb, onError) {
      const fail = streamError(`isLiked:${creationId}:${uid}`, onError);
      const refresh = async () => {
        const { data, error } = await client
          .from('likes')
          .select('uid')
          .eq('creation_id', creationId)
          .eq('uid', uid)
          .maybeSingle();
        if (error) throw error;
        cb(data !== null);
      };
      const channel = client
        .channel(channelTopic(`isLiked:${creationId}:${uid}`))
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'likes',
            filter: `creation_id=eq.${creationId}`,
          },
          () => {
            refresh().catch(fail);
          },
        );
      const closeChannel = openChannel(channel, fail);
      refresh().catch(fail);
      return closeChannel;
    },
    creationFeedback(creationId, cb, onError) {
      return pollingRowsBinding<CreationFeedback>(
        client,
        {
          label: `creationFeedback:${creationId}`,
          table: 'creation_feedback',
          registryKey: 'creations/feedback',
          eq: [['creation_id', creationId]],
          order: { column: 'version_n', ascending: false },
          intervalMs: 3_000,
        },
        cb,
        onError,
      );
    },
    comments(creationId, cb, onError) {
      return liveSetBinding<Comment>(
        client,
        {
          label: `comments:${creationId}`,
          table: 'comments',
          registryKey: 'creations/comments',
          eq: [['creation_id', creationId]],
          order: { column: 'created_at', ascending: true },
          realtimeFilter: `creation_id=eq.${creationId}`,
          keyColumns: ['id'],
        },
        cb,
        onError,
      );
    },
    openMatchInvites(creationId, cb, onError) {
      // eq (status=open) is the correctness filter, applied to the seed and to
      // every event — an UPDATE that moves a row OFF status=open now evicts it
      // (asChange synthesizes a DELETE). This drops accepted/cancelled invites
      // live whenever the event reaches the client: the HOST's own transitions
      // (RLS `own`) always do; a NON-owner watching someone else's invite get
      // accepted does NOT (RLS stops delivering the now-invisible row), so that
      // stale row lingers until the consumer's `expiresAt` filter hides it at
      // TTL (≤10 min) — a Join on it fails the accept CAS gracefully meanwhile.
      // The single realtimeFilter narrows the channel (per-game → that game's
      // rows; global → open transitions).
      return liveSetBinding<MatchInvite>(
        client,
        {
          label: `openMatchInvites:${creationId ?? 'all'}`,
          table: 'match_invites',
          registryKey: 'matchInvites',
          eq: creationId ? [['status', 'open'], ['creation_id', creationId]] : [['status', 'open']],
          order: { column: 'created_at', ascending: false },
          realtimeFilter: creationId ? `creation_id=eq.${creationId}` : `status=eq.open`,
          keyColumns: ['token'],
        },
        cb,
        onError,
      );
    },
    matchInvite(token, cb, onError) {
      // Any status: the host watches its own invite move open -> accepted.
      return liveSetBinding<MatchInvite>(
        client,
        {
          label: `matchInvite:${token}`,
          table: 'match_invites',
          registryKey: 'matchInvites',
          eq: [['token', token]],
          realtimeFilter: `token=eq.${token}`,
          keyColumns: ['token'],
        },
        (rows) => cb(rows[0] ?? null),
        onError,
      );
    },
    presence(cb, onError) {
      // No `eq` filter: freshness is TIME-based, and an eq filter can only
      // compare a column to a constant. RLS already bounds the seed to the 48h
      // retention window, and every beat is an UPDATE that keeps its row in the
      // set — so the client holds a small roster and `visiblePresence` decides
      // who is listed, re-derived on its own clock. The limit is the rail's
      // ceiling, not a correctness bound (the platform is far below it).
      return liveSetBinding<UserPresence>(
        client,
        {
          label: 'presence',
          table: 'user_presence',
          registryKey: 'userPresence',
          order: { column: 'last_seen_at', ascending: false },
          limit: 100,
          keyColumns: ['uid'],
        },
        cb,
        onError,
      );
    },
    chat(cb, onError) {
      // ONE global room, so there is nothing to scope on — no `eq`, no
      // realtimeFilter. RLS already bounds the seed to the 24h retention window
      // and to signed-in readers; the limit is the dock's page size. Newest
      // first (the natural "last 50" slice); the dock reverses for display.
      return liveSetBinding<ChatMessage>(
        client,
        {
          label: 'chat',
          table: 'chat_messages',
          registryKey: 'chatMessages',
          order: { column: 'created_at', ascending: false },
          limit: CHAT_PAGE_SIZE,
          keyColumns: ['id'],
        },
        cb,
        onError,
      );
    },
  };
}
